// Copyright (c) StrangeBee 2024

import React, { ReactElement, useMemo } from 'react';
import { Store } from 'antd/lib/form/interface';
import { useLocalMessage } from '@Helpers/';
import { FormInstance } from 'antd';
import { CustomFieldOptionsInput } from '@Organisms/CustomField/CustomFieldValueManage/CustomFieldOptionsInput';
import { THForm } from '@Molecules/THForm/THForm';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import useListQuery from '@Hooks/useListQuery';
import client from '@Client';
import { computeListSelector } from '../selector';
import { CustomFieldOutput, CustomFieldValueInput } from 'thehive-sdk';

export interface CFValuesForm {
    name: string;
    value: any;
    type: CustomFieldValueInput['type'];
    order: number;
}

interface CustomFieldValueFormProps {
    onFinish(values: CFValuesForm): void;
    form: FormInstance;
    defaultValues?: CustomFieldValueInput;
    isValid: boolean;
    onChange: () => void;
    onCancel: () => void;
}

export const CustomFieldValueForm = ({
    onFinish,
    isValid,
    form,
    defaultValues,
    onChange,
    onCancel,
}: CustomFieldValueFormProps): ReactElement | null => {
    const i = useLocalMessage();

    const { data: allCustomFields = [] } = useListQuery<CustomFieldOutput[]>({
        controller: client.CustomFieldController,
        queryParams: {
            selector: computeListSelector(),
        },
    });

    const customFieldList = useMemo(
        () => allCustomFields.sort(({ name: nameA }, { name: nameB }) => nameA.localeCompare(nameB)),
        [allCustomFields],
    );
    const onValuesChange = (changedValue: Store, allValues: CFValuesForm): void => {
        const { name } = allValues;

        const customField = customFieldList?.find((customField) => customField.name === name);

        if ('name' in changedValue) {
            form.setFieldsValue({ value: '' });
            form.setFieldsValue({ type: customField?.type });
        }

        onChange();
    };

    return (
        <THForm<CFValuesForm>
            name="custom-field-value-form"
            onFinish={onFinish}
            form={form}
            onValuesChange={onValuesChange}
            initialValues={defaultValues}
        >
            <THForm.Layout
                footer={<FormSubmitButton onCancel={onCancel} disabled={!isValid} footerText={i(`general.confirm`)} />}
            >
                <THForm.Item
                    label={i('customField.create.form.name.label')}
                    name="name"
                    rules={[{ required: true, message: i('common.required') }]}
                >
                    <THSelect
                        placeholder={i('customField.create.form.type.placeholder')}
                        options={customFieldList?.map(({ name }) => ({ label: name, value: name }))}
                    />
                </THForm.Item>

                <THForm.Item<CFValuesForm> noStyle shouldUpdate={(prev, curr) => prev.name !== curr.name}>
                    {({ getFieldValue }): ReactElement | null => {
                        const customField = customFieldList?.find(
                            (customField) => customField.name === getFieldValue('name'),
                        );

                        if (!customField) {
                            return null;
                        }

                        return (
                            <THForm.Item name="value" label={i('customField.create.form.value.label')}>
                                <CustomFieldOptionsInput customField={customField} />
                            </THForm.Item>
                        );
                    }}
                </THForm.Item>

                <THForm.Item name="type" noStyle>
                    <THInput type="hidden" />
                </THForm.Item>

                <THForm.Item name="order" noStyle>
                    <THInput type="hidden" />
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};
